import React, { useCallback, useEffect, useState } from 'react';
import {
  deleteImage,
  getMyImages,
  OFFER_PHOTO_CATEGORY,
  PHOTO_CATEGORIES,
  replaceImage,
  setCoverImage,
  updateImageCategory,
  uploadImage,
  venueImageUrl,
  VenueImage
} from '../../api';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { ACCEPTED_PHOTO_TYPES, errorMessage, Notice, NoticeBox, PageHead, photoProblem } from './shared';

const MAX_VENUE_PHOTOS = 12;

// "Image Gallery": the venue's public photo gallery and cover. Changes save immediately.
const VenueGalleryPage: React.FC = () => {
  const [images, setImages] = useState<VenueImage[] | null>(null);
  const [category, setCategory] = useState<string>(PHOTO_CATEGORIES[0]);
  const [notice, setNotice] = useState<Notice>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const refresh = useCallback(() => getMyImages().then(setImages).catch(() => setImages([])), []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The legacy single "Offer" poster isn't part of the venue gallery; offers have their own images now.
  const gallery = (images ?? []).filter((img) => img.category !== OFFER_PHOTO_CATEGORY);

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setNotice(null);
    setUploading(true);
    const problems: string[] = [];
    let added = 0;
    // Sequential so the server-side photo limit is enforced in order and errors map to files.
    for (const file of files) {
      const problem = photoProblem(file);
      if (problem) {
        problems.push(problem);
        continue;
      }
      try {
        await uploadImage(file, category);
        added++;
      } catch (err) {
        problems.push(`${file.name}: ${errorMessage(err, 'upload failed.')}`);
      }
    }
    await refresh();
    setUploading(false);
    setNotice(
      problems.length
        ? { severity: added ? 'info' : 'error', text: `${added} photo(s) added. ${problems.join(' ')}` }
        : { severity: 'success', text: `${added} photo(s) added.` }
    );
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    addFiles(files);
  };

  const handleDelete = async (image: VenueImage) => {
    if (!window.confirm('Delete this photo? It will be removed from your public listing.')) return;
    try {
      await deleteImage(image.id);
      setNotice({ severity: 'success', text: 'Photo deleted.' });
      await refresh();
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Failed to delete photo.') });
    }
  };

  const handleReplace = async (image: VenueImage, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const problem = photoProblem(file);
    if (problem) {
      setNotice({ severity: 'error', text: problem });
      return;
    }
    setUploading(true);
    try {
      const result = await replaceImage(image.id, file);
      setNotice({ severity: 'success', text: result.message });
      await refresh();
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Failed to replace photo.') });
    } finally {
      setUploading(false);
    }
  };

  const handleSetCover = async (image: VenueImage) => {
    try {
      await setCoverImage(image.id);
      setNotice({ severity: 'success', text: 'Cover photo updated.' });
      await refresh();
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Failed to set cover photo.') });
    }
  };

  const handleCategory = async (image: VenueImage, next: string) => {
    if (next === image.category) return;
    try {
      await updateImageCategory(image.id, next);
      setImages((prev) => (prev ?? []).map((img) => (img.id === image.id ? { ...img, category: next } : img)));
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Failed to update category.') });
    }
  };

  const full = gallery.length >= MAX_VENUE_PHOTOS;

  return (
    <>
      <PageHead title="Image Gallery" subtitle="Manage cover and gallery photos shown on your venue page.">
        <label className="pp-btn pp-gold" aria-disabled={uploading || full}>
          <AddIcon />Upload Images
          <input hidden multiple type="file" accept={ACCEPTED_PHOTO_TYPES.join(',')} disabled={uploading || full} onChange={handleFileInput} />
        </label>
      </PageHead>

      <div className="pp-panel">
        <NoticeBox notice={notice} />
        <div className="pp-field" style={{ maxWidth: 240, marginBottom: 12 }}>
          <label htmlFor="photo-category">Category for new photos</label>
          <select id="photo-category" className="pp-input" value={category} onChange={(e) => setCategory(e.target.value)}>
            {PHOTO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <label
          className={`pp-drop ${dragging ? 'drag' : ''}`}
          style={{ cursor: 'pointer' }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(Array.from(e.dataTransfer.files)); }}
        >
          <div className="pp-drop-row"><CloudUploadOutlinedIcon /><b>Add Venue Photos</b></div>
          <p className="pp-muted" style={{ margin: 0 }}>{uploading ? 'Uploading...' : full ? `Gallery full (${MAX_VENUE_PHOTOS} photos). Delete one to add another.` : 'Drag & drop images here, or click to browse'}</p>
          <small className="pp-muted">JPG, PNG or WebP · 5 MB each · Up to {MAX_VENUE_PHOTOS} photos</small>
          <input hidden multiple type="file" accept={ACCEPTED_PHOTO_TYPES.join(',')} disabled={uploading || full} onChange={handleFileInput} />
        </label>

        {images === null ? (
          <p className="pp-muted">Loading photos...</p>
        ) : gallery.length === 0 ? (
          <p className="pp-muted" style={{ textAlign: 'center', marginTop: 20 }}>No photos yet. Add a few to make your listing stand out; the first one becomes your cover.</p>
        ) : (
          <div className="pp-gallery">
            {gallery.map((img) => (
              <div key={img.id}>
                <div className={`pp-photo ${img.isCover ? 'is-cover' : ''}`}>
                  <img src={venueImageUrl(img.url)} alt={img.category} loading="lazy" />
                  {img.isCover && <span className="pp-thumb-tag">COVER</span>}
                  <div className="pp-controls">
                    {img.isCover ? <span /> : <button className="pp-mini" onClick={() => handleSetCover(img)}>Set Cover</button>}
                    <span style={{ display: 'flex', gap: 4 }}>
                      <label className="pp-mini">
                        Replace
                        <input hidden type="file" accept={ACCEPTED_PHOTO_TYPES.join(',')} disabled={uploading} onChange={(e) => handleReplace(img, e)} />
                      </label>
                      <button className="pp-mini" onClick={() => handleDelete(img)}>Delete</button>
                    </span>
                  </div>
                </div>
                <select className="pp-input pp-photo-select" aria-label="Photo category" value={img.category} onChange={(e) => handleCategory(img, e.target.value)}>
                  {PHOTO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        <p className="pp-hint" style={{ marginTop: 14 }}>Changes are saved as you make them.</p>
      </div>
    </>
  );
};

export default VenueGalleryPage;
